import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Paper,
  TextField,
  InputAdornment,
  LinearProgress,
  Tab,
  Tabs,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  makeStyles
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import SearchIcon from '@material-ui/icons/Search';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { showGlobalToast } from 'src/features/uiSlice';
import { messageToastType_const } from 'src/app/constant/config';
import axiosInterceptor from 'src/app/axiosInterceptor';
import { isAgencyCustomerOrder } from './orderUtils';

const useStyles = makeStyles(theme => ({
  sectionTitle: {
    fontWeight: 700,
    fontSize: 14,
    color: '#3f51b5',
    marginBottom: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },
  infoBox: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: '12px 16px',
    border: '1px solid #e9ecef',
    height: '100%'
  },
  label: {
    fontSize: 12,
    color: '#6c757d',
    fontWeight: 500,
    marginBottom: 2
  },
  value: {
    fontSize: 13,
    color: '#212529',
    fontWeight: 600,
    wordBreak: 'break-word'
  },
  tableHeader: {
    backgroundColor: '#f1f3f9',
    fontWeight: 700,
    fontSize: 12,
    color: '#495057'
  },
  tableCell: {
    fontSize: 12,
    padding: '8px 12px'
  },
  serialChip: {
    margin: 2,
    height: 22,
    fontSize: 11,
    fontFamily: 'monospace'
  }
}));

const getImageUrl = url => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  const imgServer = process.env.REACT_APP_IMG_SERVER || process.env.REACT_APP_BACKEND_URL || '';
  const cleanServer = imgServer.replace(/\/+$/, '');
  const cleanUrl = url.replace(/^\/+/, '');
  return `${cleanServer}/${cleanUrl}`;
};

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const OrderDetailDialog = ({
  open,
  onClose,
  order: initialOrder,
  STATUS_CONFIG = EMPTY_OBJECT,
  listDeviceType = EMPTY_ARRAY,
  formatMoney = amount => `${amount}`,
  getPaymentStyle = () => ({ color: '#666', bg: '#f5f5f5' }),
  onEditOrder,
  onOpenSerialModal,
  setSelectedGallery,
  isAdmin,
  isAccountant,
  isSale,
  isLeadMarketing,
  isAgency,
  canManageReceipts,
  receipts = EMPTY_ARRAY,
  agencyDebtMap = EMPTY_OBJECT,
  onEditReceipt,
  onDeleteReceipt,
  onOpenAddReceipt,
  isAnySale: propIsAnySale
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [serialModal, setSerialModal] = useState({ open: false, typeName: '', serials: [] });
  const [serialSearch, setSerialSearch] = useState('');
  const [localReceipts, setLocalReceipts] = useState([]);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  const [receiptTab, setReceiptTab] = useState(0);
  const [receiptMenuAnchor, setReceiptMenuAnchor] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  const orderId = initialOrder?.id;
  const [activeOrder, setActiveOrder] = useState(initialOrder);

  const isAnySale = Boolean(propIsAnySale || isSale || isLeadMarketing);

  const effectiveCanManageReceipts = canManageReceipts !== undefined
    ? canManageReceipts
    : Boolean((isAdmin || isAccountant) && !isSale && !isLeadMarketing && !isAgency);

  const handleReceiptMenuOpen = (e, receipt) => {
    e.stopPropagation();
    setReceiptMenuAnchor(e.currentTarget);
    setSelectedReceipt(receipt);
  };

  const handleReceiptMenuClose = () => {
    setReceiptMenuAnchor(null);
    setSelectedReceipt(null);
  };

  useEffect(() => {
    setActiveOrder(initialOrder);
    if (!open || !orderId) return;
    if (!initialOrder?.order_device_types && !initialOrder?.devices) {
      axiosInterceptor
        .get(`/order/${orderId}`)
        .then(res => {
          const payload = res.data?.payload || res.data;
          if (payload && payload.id) {
            setActiveOrder(payload);
          }
        })
        .catch(() => {});
    }
  }, [open, orderId]);

  useEffect(() => {
    if (!open || !orderId) return;
    if (isAnySale) {
      setLocalReceipts([]);
      return;
    }
    setReceiptTab(0);

    const fromOrder = initialOrder?.receipts || initialOrder?.payment_receipts || [];
    const fromProps = receipts || [];
    const map = new Map();
    [...fromOrder, ...fromProps].forEach(r => {
      if (r && (r.id || r.code)) map.set(String(r.id || r.code), r);
    });
    setLocalReceipts(Array.from(map.values()));

    setLoadingReceipts(true);
    axiosInterceptor
      .get(`/receipt?order_id=${orderId}&page_size=50`)
      .then(res => {
        const payload = res.data?.payload;
        const fetched = payload?.receipts || payload?.data || (Array.isArray(payload) ? payload : []);
        if (Array.isArray(fetched) && fetched.length > 0) {
          const m = new Map();
          fetched.forEach(r => {
            if (r && (r.id || r.code)) m.set(String(r.id || r.code), r);
          });
          setLocalReceipts(Array.from(m.values()));
        }
      })
      .catch(() => {})
      .finally(() => {
        setLoadingReceipts(false);
      });
  }, [open, orderId, isAnySale]);

  if (!initialOrder) return null;
  const order = activeOrder || initialOrder;

  const statusCfg = STATUS_CONFIG[order.status] || STATUS_CONFIG[0] || {
    label: 'Chưa xác định',
    color: '#666',
    bg: '#f5f5f5'
  };

  const isAgencyCustomer = isAgencyCustomerOrder(order);
  const agId = String(order.agency_id || order.agency?.id || '');
  const agNameKey = (order.agency?.name || (isAgencyCustomer ? order.customer_name : '') || '').trim().toLowerCase();
  const agencyInfo = (isAgencyCustomer && agencyDebtMap) ? (agencyDebtMap[agId] || agencyDebtMap[agNameKey] || null) : null;

  const customerName =
    order.end_user?.full_name ||
    (!isAgencyCustomer ? (order.customer_name || order.customerName) : '') ||
    order.agency?.name ||
    order.customer_name ||
    order.customerName ||
    '--';
  const customerPhone =
    order.customer_phone ||
    order.phone ||
    order.phone_number ||
    order.customerPhone ||
    order.end_user?.phone ||
    order.end_user?.phone_number ||
    (isAgencyCustomer ? order.agency?.leader?.phone || order.agency?.phone : '') ||
    '--';
  const customerAddress =
    order.address ||
    order.customer_address ||
    order.shipping_address ||
    order.end_user?.address ||
    (isAgencyCustomer ? order.agency?.leader?.address || order.agency?.address || order.agency_name : '') ||
    '--';

  const saleName =
    order.sale?.full_name ||
    order.user?.username ||
    (order.sale_name || order.saleName || 'Hệ thống').split('(')[0].trim();

  const isPartSale =
    order.buy_method === 'Bán phụ kiện' ||
    order.buyMethod === 'Bán phụ kiện' ||
    order.order_type === 'PART_SALE' ||
    order.orderType === 'PART_SALE';

  const paymentStyle = getPaymentStyle(order.payment_method || order.paymentMethod);

  // Equipment & Accessories Subtotals calculation
  const deviceList = order.order_device_types || order.devices || [];
  const productList = order.order_products || order.products || [];

  const deviceSubtotal = deviceList.reduce(
    (sum, d) => sum + (d.quantity || 0) * (d.unit_price || d.unitPrice || 0),
    0
  );
  const productSubtotal = productList.reduce(
    (sum, p) => sum + (p.quantity || 0) * (p.unit_price || p.unitPrice || 0) - (p.discount || 0),
    0
  );

  const discount = Number(order.discount || 0);
  const extraFee = Number(order.extra_fee || 0);
  const vatRate = Number(order.vat || 0);
  const totalBase = deviceSubtotal + productSubtotal;
  const taxableBase = Math.max(0, totalBase - discount);
  const vatAmount = taxableBase * (vatRate / 100);
  const grandTotal = Number(order.total_amount || order.totalAmount || (taxableBase + vatAmount + extraFee));

  const combinedReceipts = isAgencyCustomer && agencyInfo && (agencyInfo.receipts || []).length > 0
    ? agencyInfo.receipts
    : localReceipts;
  const receiptsSum = combinedReceipts.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const displayedReceipts = (isAgencyCustomer && agencyInfo && receiptTab === 0)
    ? (agencyInfo.receipts || [])
    : localReceipts;

  // Financial info of the current order
  const orderReceiptsSum = (localReceipts || []).reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const bePaymentStatus = order.payment_status;
  const hasBeStatus = typeof bePaymentStatus === 'string' && Boolean(bePaymentStatus);

  const rawPaid = typeof order.paid === 'number' ? order.paid : (typeof order.paid_amount === 'number' ? order.paid_amount : undefined);
  let orderPaidAmount = typeof rawPaid === 'number' ? Math.max(rawPaid, orderReceiptsSum) : orderReceiptsSum;

  const orderDebtAmount = typeof order.debt_amount === 'number'
    ? order.debt_amount
    : Math.max(0, grandTotal - orderPaidAmount);
  const orderOverAmount = !isAgencyCustomer && grandTotal > 0 && orderPaidAmount > grandTotal
    ? orderPaidAmount - grandTotal
    : 0;

  let orderPaymentStatus = 'UNPAID';
  if (order.status === -1) {
    orderPaymentStatus = 'CANCELLED';
  } else if (hasBeStatus) {
    orderPaymentStatus = String(bePaymentStatus).toUpperCase();
  } else if (orderPaidAmount > grandTotal && grandTotal > 0) {
    orderPaymentStatus = 'OVERPAID';
  } else if (orderPaidAmount === grandTotal && grandTotal > 0) {
    orderPaymentStatus = 'PAID';
  } else if (orderPaidAmount > 0) {
    orderPaymentStatus = 'PARTIAL';
  }

  const paidAmount = isAgencyCustomer && typeof order.paid_amount === 'number' ? order.paid_amount : orderPaidAmount;
  const debtAmount = isAgencyCustomer && typeof order.debt_amount === 'number' ? order.debt_amount : orderDebtAmount;
  const overAmount = orderOverAmount;
  const paymentStatus = orderPaymentStatus;
  const completionPercent = grandTotal > 0 ? Math.round((paidAmount / grandTotal) * 100) : 0;

  const handleCreateReceipt = () => {
    if (onOpenAddReceipt) {
      const isAgencyCust = isAgencyCustomer;
      const pId = isAgencyCust
        ? order.agency_id || order.agency?.id || agencyInfo?.id
        : order.end_user_id || order.end_user?.id || order.customer_id;
      const pName = customerName !== '--' ? customerName : '';
      const pPhone = customerPhone !== '--' ? customerPhone : '';
      const pAddr = customerAddress !== '--' ? customerAddress : '';
      const orderCode = order.code || order.order_code || order.orderCode || order.id || '';
      const agencyDebt = agencyInfo?.debt !== undefined ? agencyInfo.debt : 0;

      onClose();
      if (isAgencyCust) {
        onOpenAddReceipt({
          payer_type: 'AGENCY',
          payer_id: pId,
          payer_name: pName,
          customer_name: pName,
          customer_phone: pPhone,
          phone: pPhone,
          address: pAddr,
          company: pAddr,
          amount: agencyDebt > 0 ? agencyDebt : 0,
          agency_debt: agencyDebt,
          note: `Thu tiền công nợ Đại lý ${pName}`
        });
      } else {
        onOpenAddReceipt({
          order_id: order.id,
          order_code: orderCode,
          payer_type: 'END_USER',
          payer_id: pId,
          payer_name: pName,
          customer_name: pName,
          customer_phone: pPhone,
          phone: pPhone,
          address: pAddr,
          company: pAddr,
          amount: debtAmount > 0 ? debtAmount : grandTotal,
          order_debt: debtAmount > 0 ? debtAmount : grandTotal,
          order_total: grandTotal,
          note: orderCode ? `Thu tiền đơn hàng #${orderCode}` : ''
        });
      }
    }
  };

  const filteredSerials = (serialModal.serials || []).filter(s =>
    String(s || '').toLowerCase().includes(serialSearch.trim().toLowerCase())
  );

  const handleCopyText = (text, labelName) => {
    if (!text || text === '--') return;
    navigator.clipboard.writeText(text);
    dispatch(
      showGlobalToast({
        message: `Đã sao chép ${labelName}: ${text}`,
        type: messageToastType_const.success
      })
    );
  };

  const handleCopyOrderInfo = () => {
    const name = customerName;
    const phone = customerPhone;
    const address = customerAddress;

    const deviceList = order.order_device_types || order.devices || [];
    const productList = order.order_products || order.products || [];

    const productsArr = [];
    const quantitiesArr = [];
    const pricesArr = [];

    deviceList.forEach(d => {
      const typeName =
        d.device_type?.name ||
        d.device_type_name ||
        d.type ||
        (listDeviceType.find(lt => lt.id === d.device_type_id)?.name) ||
        'Thiết bị VGPS';
      productsArr.push(typeName);
      quantitiesArr.push(d.quantity || 1);
      pricesArr.push(formatMoney(d.unit_price || d.unitPrice || 0));
    });

    productList.forEach(p => {
      const pName = (p.product_code ? `[${p.product_code}] ` : '') + (p.product_name || p.product?.name || 'Phụ kiện');
      productsArr.push(pName);
      quantitiesArr.push(p.quantity || 1);
      pricesArr.push(formatMoney(p.unit_price || p.unitPrice || 0));
    });

    const productStr = productsArr.join(', ') || '--';
    const quantityStr = quantitiesArr.join(', ') || '--';
    const priceStr = pricesArr.join(', ') || '--';

    const activeMethod = (order.active_method || order.activeMethod || '--').replace(/Vgps/g, 'VGPS');
    const simType = (order.sim_method || order.simMethod || '--').replace(/Vgps/g, 'VGPS');

    const copyText = [
      `Tên khách hàng: ${name}`,
      `Số điện thoại: ${phone}`,
      `Sản phẩm: ${productStr}`,
      `Số lượng: ${quantityStr}`,
      `Đơn giá (VNĐ): ${priceStr}`,
      `Địa chỉ: ${address}`,
      `Gói cước: ${activeMethod}`,
      `Loại SIM: ${simType}`
    ].join('\n');

    navigator.clipboard.writeText(copyText);
    dispatch(
      showGlobalToast({
        message: 'Đã sao chép thông tin đơn hàng!',
        type: messageToastType_const.success
      })
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle disableTypography style={{ padding: '16px 24px', borderBottom: '1px solid #e0e3ef' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
              <Typography variant="h6" style={{ fontWeight: 700, color: '#1a1f36' }}>
                Chi tiết đơn hàng #{order.code || order.id}
              </Typography>
              <Chip
                label={statusCfg.label}
                size="small"
                style={{
                  backgroundColor: statusCfg.bg,
                  color: statusCfg.color,
                  fontWeight: 700,
                  border: `1px solid ${statusCfg.color}44`
                }}
              />
            </Box>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              {onOpenAddReceipt && effectiveCanManageReceipts && !isAnySale && order?.status !== -1 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCreateReceipt}
                  style={{ fontWeight: 600, textTransform: 'none', color: '#2e7d32', borderColor: '#2e7d32' }}
                >
                  Lập phiếu thu
                </Button>
              )}
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<FileCopyIcon />}
                onClick={handleCopyOrderInfo}
                style={{ fontWeight: 600, textTransform: 'none' }}
              >
                Sao chép
              </Button>

              {onOpenSerialModal && (isAdmin || isAccountant || isSale || isLeadMarketing) && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    onClose();
                    onOpenSerialModal(order);
                  }}
                  style={{ fontWeight: 600, textTransform: 'none' }}
                >
                  Quản lý Serial
                </Button>
              )}
              {onEditOrder && (isAdmin || isSale || isLeadMarketing) && order.status < 4 && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    onClose();
                    onEditOrder(order);
                  }}
                  style={{ fontWeight: 600, textTransform: 'none' }}
                >
                  Sửa phiếu
                </Button>
              )}
              <IconButton size="small" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent style={{ padding: 24 }}>
          <Box style={{ backgroundColor: '#fff' }}>
            <Grid container spacing={2} style={{ marginBottom: 16 }}>
              {/* Customer Information */}
              <Grid item xs={12} sm={6}>
                <div className={classes.infoBox}>
                  <Typography className={classes.sectionTitle}>
                    Thông tin khách hàng
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <div className={classes.label}>Tên đại lý / Khách hàng:</div>
                      <div
                        className={classes.value}
                        onClick={() => handleCopyText(customerName, 'tên khách hàng')}
                        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <Tooltip title="Click để sao chép">
                          <span>{customerName}</span>
                        </Tooltip>
                        <FileCopyOutlinedIcon style={{ fontSize: 13, color: '#888' }} />
                      </div>
                    </Grid>
                    <Grid item xs={6}>
                      <div className={classes.label}>Số điện thoại:</div>
                      <div
                        className={classes.value}
                        onClick={() => handleCopyText(customerPhone, 'số điện thoại')}
                        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <Tooltip title="Click để sao chép">
                          <span>{customerPhone}</span>
                        </Tooltip>
                        {customerPhone !== '--' && <FileCopyOutlinedIcon style={{ fontSize: 13, color: '#888' }} />}
                      </div>
                    </Grid>
                    <Grid item xs={6}>
                      <div className={classes.label}>Loại hình:</div>
                      <div className={classes.value}>
                        {isAgencyCustomer ? 'Đại lý phân phối' : 'Khách lẻ'}
                      </div>
                    </Grid>
                    <Grid item xs={12}>
                      <div className={classes.label}>Địa chỉ:</div>
                      <div
                        className={classes.value}
                        onClick={() => handleCopyText(customerAddress, 'địa chỉ')}
                        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <Tooltip title="Click để sao chép">
                          <span>{customerAddress}</span>
                        </Tooltip>
                        {customerAddress !== '--' && <FileCopyOutlinedIcon style={{ fontSize: 13, color: '#888' }} />}
                      </div>
                    </Grid>
                  </Grid>
                </div>
              </Grid>

              {/* Sale & Order Metadata */}
              <Grid item xs={12} sm={6}>
                <div className={classes.infoBox}>
                  <Typography className={classes.sectionTitle}>
                    Thông tin xử lý đơn hàng
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <div className={classes.label}>Nhân viên Sale:</div>
                      <div className={classes.value}>{saleName}</div>
                      {order.created_by && (order.creator?.full_name || order.created_by_name) && order.created_by !== (order.sale_id || order.sale?.id) && (
                        <div style={{ fontSize: 11, color: '#d97706', marginTop: 2 }}>
                          (Tạo bởi: {order.creator?.full_name || order.created_by_name})
                        </div>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <div className={classes.label}>Hình thức thanh toán:</div>
                      <div>
                        <Chip
                          label={order.payment_method || order.paymentMethod || 'Tiền mặt'}
                          size="small"
                          style={{
                            backgroundColor: paymentStyle.bg,
                            color: paymentStyle.color,
                            fontWeight: 700,
                            fontSize: 11
                          }}
                        />
                      </div>
                    </Grid>
                    <Grid item xs={6}>
                      <div className={classes.label}>Hình thức kích hoạt:</div>
                      <div
                        className={classes.value}
                        onClick={() => handleCopyText((order.active_method || order.activeMethod || '').replace(/Vgps/g, 'VGPS'), 'gói cước')}
                        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <Tooltip title="Click để sao chép">
                          <span>{(order.active_method || order.activeMethod || '--').replace(/Vgps/g, 'VGPS')}</span>
                        </Tooltip>
                        <FileCopyOutlinedIcon style={{ fontSize: 13, color: '#888' }} />
                      </div>
                    </Grid>
                    <Grid item xs={6}>
                      <div className={classes.label}>Loại SIM:</div>
                      <div
                        className={classes.value}
                        onClick={() => handleCopyText((order.sim_method || order.simMethod || '').replace(/Vgps/g, 'VGPS'), 'loại SIM')}
                        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <Tooltip title="Click để sao chép">
                          <span style={{ whiteSpace: 'pre-line' }}>
                            {(order.sim_method || order.simMethod || '--')
                              .replace(/Vgps/g, 'VGPS')
                              .split(';')
                              .map(s => s.trim())
                              .filter(Boolean)
                              .join('\n')}
                          </span>
                        </Tooltip>
                        <FileCopyOutlinedIcon style={{ fontSize: 13, color: '#888' }} />
                      </div>
                    </Grid>
                    <Grid item xs={6}>
                      <div className={classes.label}>Phân loại đơn:</div>
                      <div className={classes.value}>
                        {(order.buy_method || order.buyMethod || '--').replace(/Vgps/g, 'VGPS')}
                      </div>
                    </Grid>
                    <Grid item xs={6}>
                      <div className={classes.label}>Hình thức lắp đặt:</div>
                      <div className={classes.value}>
                        {(order.installer || '--').replace(/Vgps/g, 'VGPS')}
                      </div>
                    </Grid>
                  </Grid>
                </div>
              </Grid>
            </Grid>

            {/* Section: Device Types & Serials */}
            {deviceList.length > 0 && (
              <Box mb={2}>
                <Typography className={classes.sectionTitle}>
                  Danh sách thiết bị VGPS
                </Typography>
                <Paper variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.tableHeader}>#</TableCell>
                        <TableCell className={classes.tableHeader}>Tên loại thiết bị</TableCell>
                        <TableCell className={classes.tableHeader} align="center">Số lượng</TableCell>
                        <TableCell className={classes.tableHeader} align="right">Đơn giá</TableCell>
                        <TableCell className={classes.tableHeader} align="right">Thành tiền</TableCell>
                        <TableCell className={classes.tableHeader}>Danh sách Serial / IMEI</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deviceList.map((d, index) => {
                        const typeName =
                          d.device_type?.name ||
                          d.device_type_name ||
                          d.type ||
                          listDeviceType.find(lt => lt.id === d.device_type_id)?.name ||
                          'Thiết bị VGPS';

                        const rawSerials =
                          d.order_details?.map(od => od.serial || od.device?.serial || od.device?.imei) ||
                          d.serials ||
                          [];
                        const serials = rawSerials.filter(Boolean);

                        const qty = d.quantity || 0;
                        const price = d.unit_price || d.unitPrice || 0;

                        return (
                          <TableRow key={index} hover>
                            <TableCell className={classes.tableCell}>{index + 1}</TableCell>
                            <TableCell className={classes.tableCell}>
                              <Typography
                                onClick={() => handleCopyText(typeName, 'tên thiết bị')}
                                style={{ fontWeight: 600, fontSize: 13, color: '#1a73e8', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                              >
                                <Tooltip title="Click để sao chép">
                                  <span>{typeName}</span>
                                </Tooltip>
                                <FileCopyOutlinedIcon style={{ fontSize: 12, color: '#1a73e8' }} />
                              </Typography>
                            </TableCell>
                            <TableCell className={classes.tableCell} align="center">
                              <Tooltip title="Click để sao chép số lượng">
                                <strong
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleCopyText(String(qty), 'số lượng')}
                                >
                                  {qty}
                                </strong>
                              </Tooltip>
                            </TableCell>
                            <TableCell className={classes.tableCell} align="right">
                              <Tooltip title="Click để sao chép đơn giá">
                                <span
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleCopyText(formatMoney(price), 'đơn giá')}
                                >
                                  {formatMoney(price)}
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell className={classes.tableCell} align="right">
                              <strong>{formatMoney(qty * price)}</strong>
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              {serials.length > 0 ? (
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<VisibilityIcon style={{ fontSize: 13 }} />}
                                  onClick={() => {
                                    setSerialSearch('');
                                    setSerialModal({
                                      open: true,
                                      typeName,
                                      serials
                                    });
                                  }}
                                  style={{
                                    backgroundColor: '#d32f2f',
                                    color: '#ffffff',
                                    fontSize: 11,
                                    height: 24,
                                    padding: '0 10px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderRadius: 12,
                                    boxShadow: 'none'
                                  }}
                                >
                                  Xem serial ({serials.length})
                                </Button>
                              ) : (
                                <Typography variant="caption" color="textSecondary">
                                  Chưa gán Serial
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Paper>
              </Box>
            )}

            {/* Section: Accessory Products */}
            {productList.length > 0 && (
              <Box mb={2}>
                <Typography className={classes.sectionTitle}>
                  Danh sách phụ kiện
                </Typography>
                <Paper variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.tableHeader}>#</TableCell>
                        <TableCell className={classes.tableHeader}>Mã / Tên phụ kiện</TableCell>
                        <TableCell className={classes.tableHeader} align="center">Số lượng</TableCell>
                        <TableCell className={classes.tableHeader} align="right">Đơn giá</TableCell>
                        <TableCell className={classes.tableHeader} align="right">Chiết khấu</TableCell>
                        <TableCell className={classes.tableHeader} align="right">Thành tiền</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productList.map((p, index) => {
                        const pCode = p.product_code || p.product?.code || '';
                        const pName = p.product_name || p.product?.name || '';
                        const qty = p.quantity || 1;
                        const price = p.unit_price || p.unitPrice || 0;
                        const discount = p.discount || 0;
                        const sub = qty * price - discount;

                        return (
                          <TableRow key={index} hover>
                            <TableCell className={classes.tableCell}>{index + 1}</TableCell>
                            <TableCell className={classes.tableCell}>
                              <Typography style={{ fontWeight: 600, fontSize: 13, color: '#2e7d32' }}>
                                {pCode ? `[${pCode}] ` : ''}{pName}
                              </Typography>
                            </TableCell>
                            <TableCell className={classes.tableCell} align="center">
                              <strong>{qty}</strong>
                            </TableCell>
                            <TableCell className={classes.tableCell} align="right">
                              {formatMoney(price)}
                            </TableCell>
                            <TableCell className={classes.tableCell} align="right">
                              {discount > 0 ? formatMoney(discount) : '0 đ'}
                            </TableCell>
                            <TableCell className={classes.tableCell} align="right">
                              <strong>{formatMoney(sub)}</strong>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Paper>
              </Box>
            )}

            {/* Logistics & Tracking */}
            <Grid container spacing={2} style={{ marginBottom: 16 }}>
              <Grid item xs={12} sm={6}>
                <div className={classes.infoBox}>
                  <Typography className={classes.sectionTitle}>
                    Vận chuyển & Vận đơn
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <div className={classes.label}>Mã / Link vận đơn:</div>
                      <div className={classes.value}>
                        {order.tracking_number ? (
                          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                            <span>{order.tracking_number}</span>
                            <IconButton
                              size="small"
                              onClick={() => {
                                let url = order.tracking_number;
                                if (!/^https?:\/\//i.test(url)) url = 'http://' + url;
                                window.open(url, '_blank');
                              }}
                            >
                              <OpenInNewIcon style={{ fontSize: 14, color: '#1a73e8' }} />
                            </IconButton>
                          </Box>
                        ) : (
                          'Chưa có link vận đơn'
                        )}
                      </div>
                    </Grid>
                    <Grid item xs={6}>
                      <div className={classes.label}>Trạng thái giao hàng:</div>
                      <div className={classes.value}>
                        {order.status >= 3 ? (
                          <Chip
                            icon={<CheckCircleIcon style={{ fontSize: 14, color: '#fff' }} />}
                            label="Đã giao hàng"
                            size="small"
                            style={{ background: '#27ae60', color: '#fff', fontWeight: 600, fontSize: 11 }}
                          />
                        ) : (
                          <Chip
                            label="Chưa giao"
                            size="small"
                            style={{ background: '#f5f5f5', color: '#666', fontSize: 11 }}
                          />
                        )}
                      </div>
                    </Grid>
                  </Grid>
                </div>
              </Grid>

              {/* Note */}
              <Grid item xs={12} sm={6}>
                <div className={classes.infoBox}>
                  <Typography className={classes.sectionTitle}>
                    Ghi chú đơn hàng
                  </Typography>
                  <Typography variant="body2" style={{ whiteSpace: 'pre-line', color: '#333', fontSize: 13 }}>
                    {order.description || order.note || 'Không có ghi chú.'}
                  </Typography>
                </div>
              </Grid>
            </Grid>

            {/* Installation Images Preview */}
            {Array.isArray(order.order_images) && order.order_images.length > 0 && (
              <Box mb={2}>
                <Typography className={classes.sectionTitle}>
                  Hình ảnh nghiệm thu / Lắp đặt ({order.order_images.length})
                </Typography>
                <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                  {order.order_images.map((img, idx) => (
                    <Box
                      key={idx}
                      onClick={() => {
                        if (setSelectedGallery) {
                          setSelectedGallery({
                            images: order.order_images,
                            index: idx,
                            orderId: order.id,
                            order_status: order.status
                          });
                        }
                      }}
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: 6,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: '1px solid #ddd',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                      }}
                    >
                      <img
                        src={getImageUrl(img.url)}
                        alt={`order-img-${idx}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Financial Breakdown Summary */}
            <Divider style={{ margin: '16px 0' }} />
            <Box display="flex" justifyContent="flex-end">
              <Box style={{ minWidth: 280 }}>
                {deviceSubtotal > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="textSecondary">Tạm tính thiết bị:</Typography>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>{formatMoney(deviceSubtotal)}</Typography>
                  </Box>
                )}
                {productSubtotal > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="textSecondary">Tạm tính phụ kiện:</Typography>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>{formatMoney(productSubtotal)}</Typography>
                  </Box>
                )}
                {discount > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="textSecondary">Chiết khấu:</Typography>
                    <Typography variant="body2" style={{ fontWeight: 600, color: '#d32f2f' }}>
                      -{formatMoney(discount)}
                    </Typography>
                  </Box>
                )}
                {vatRate > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="textSecondary">Thuế VAT ({vatRate}%):</Typography>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      +{formatMoney(vatAmount)}
                    </Typography>
                  </Box>
                )}
                {extraFee !== 0 && (
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="textSecondary">Phí ship & Lắp đặt:</Typography>
                    <Typography variant="body2" style={{ fontWeight: 600, color: '#e67e22' }}>
                      -{formatMoney(Math.abs(extraFee))}
                    </Typography>
                  </Box>
                )}
                <Divider style={{ margin: '8px 0' }} />
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#1a1f36' }}>
                    Tổng tiền thanh toán:
                  </Typography>
                  <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#d32f2f' }}>
                    {formatMoney(grandTotal)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* ── Lịch sử thanh toán & Phiếu thu (Ẩn đối với Sale / Lead marketing) ── */}
            {!isAnySale && (
              <>
                <Divider style={{ margin: '24px 0 16px 0' }} />
                <Box mb={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                        Lịch sử thanh toán & Phiếu thu ({combinedReceipts.length})
                      </Typography>
                      {isAgencyCustomer ? (
                        agencyInfo?.debt <= 0 && (agencyInfo?.totalSales || 0) > 0 ? (
                          <Chip
                            label="Đại lý hết nợ"
                            size="small"
                            style={{ background: '#eafaf1', color: '#27ae60', fontWeight: 700, height: 22 }}
                          />
                        ) : (agencyInfo?.debt || 0) > 0 ? (
                          <Chip
                            label={`Đại lý nợ: ${formatMoney(agencyInfo.debt)}`}
                            size="small"
                            style={{ background: '#fff1f0', color: '#cf1322', fontWeight: 700, height: 22 }}
                          />
                        ) : (
                          <Chip
                            label="Chưa phát sinh nợ"
                            size="small"
                            style={{ background: '#f5f5f5', color: '#666', fontWeight: 600, height: 22 }}
                          />
                        )
                      ) : paymentStatus === 'OVERPAID' || overAmount > 0 ? (
                        <Chip
                          label={`Thu thừa: +${formatMoney(overAmount)}`}
                          size="small"
                          style={{ background: '#f3e8ff', color: '#7e22ce', fontWeight: 700, height: 22, border: '1px solid #d8b4fe' }}
                        />
                      ) : paymentStatus === 'PAID' ? (
                        <Chip
                          label="Đã thu đủ"
                          size="small"
                          style={{ background: '#eafaf1', color: '#27ae60', fontWeight: 700, height: 22 }}
                        />
                      ) : paymentStatus === 'PARTIAL' ? (
                        <Chip
                          label={`Thiếu: ${formatMoney(debtAmount)}`}
                          size="small"
                          style={{ background: '#fef9e7', color: '#e67e22', fontWeight: 700, height: 22 }}
                        />
                      ) : (
                        <Chip
                          label="Chưa thanh toán"
                          size="small"
                          style={{ background: '#fff1f0', color: '#cf1322', fontWeight: 600, height: 22 }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Agency Debt Overview Banner */}
                  {isAgencyCustomer && agencyInfo && (
                    <Box
                      mb={2}
                      p={1.5}
                      style={{
                        backgroundColor: '#f8fafc',
                        borderRadius: 8,
                        border: '1px solid #dbeafe',
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 12
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#1e40af' }}>
                          Công nợ tổng hợp Đại lý: {agencyInfo.name || customerName}
                        </Typography>
                        <Typography variant="caption" style={{ color: '#64748b' }}>
                          (Tổng hợp trên {agencyInfo.orders?.length || 0} đơn hàng & {agencyInfo.receipts?.length || 0} phiếu thu)
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" style={{ gap: 16 }}>
                        <Box textAlign="right">
                          <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>Tổng mua</Typography>
                          <Typography variant="body2" style={{ fontWeight: 700 }}>{formatMoney(agencyInfo.totalSales)}</Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="caption" style={{ color: '#2e7d32', display: 'block' }}>Đã thu</Typography>
                          <Typography variant="body2" style={{ fontWeight: 700, color: '#2e7d32' }}>{formatMoney(agencyInfo.totalPaid)}</Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="caption" style={{ color: agencyInfo.debt > 0 ? '#d32f2f' : '#2e7d32', display: 'block', fontWeight: 600 }}>
                            {agencyInfo.debt > 0 ? 'Còn nợ' : 'Đã thanh toán đủ'}
                          </Typography>
                          <Typography variant="body2" style={{ fontWeight: 700, color: agencyInfo.debt > 0 ? '#d32f2f' : '#2e7d32' }}>
                            {formatMoney(agencyInfo.debt)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* 3 Summary Cards - Thể hiện thông tin tài chính của riêng đơn hàng đang xem */}
                  <Grid container spacing={2} style={{ marginBottom: 16 }}>
                    <Grid item xs={4}>
                      <Box p={1.5} style={{ background: '#f8f9fa', borderRadius: 8, border: '1px solid #e9ecef', textAlign: 'center' }}>
                        <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                          Tổng tiền đơn hàng
                        </Typography>
                        <Typography style={{ fontSize: 15, fontWeight: 700, color: '#1a1f36', marginTop: 2 }}>
                          {formatMoney(grandTotal)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box p={1.5} style={{ background: '#f1f8e9', borderRadius: 8, border: '1px solid #c8e6c9', textAlign: 'center' }}>
                        <Typography variant="caption" style={{ color: '#2e7d32', fontWeight: 600 }}>
                          Đã thanh toán
                        </Typography>
                        <Typography style={{ fontSize: 15, fontWeight: 700, color: '#2e7d32', marginTop: 2 }}>
                          {formatMoney(paidAmount)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box
                        p={1.5}
                        style={{
                          background:
                            overAmount > 0
                              ? '#f3e8ff'
                              : debtAmount === 0 && grandTotal > 0
                              ? '#f1f8e9'
                              : paidAmount > 0
                              ? '#fff8e1'
                              : '#fff1f0',
                          borderRadius: 8,
                          border: `1px solid ${
                            overAmount > 0
                              ? '#d8b4fe'
                              : debtAmount === 0 && grandTotal > 0
                              ? '#c8e6c9'
                              : paidAmount > 0
                              ? '#ffe082'
                              : '#ffa39e'
                          }`,
                          textAlign: 'center'
                        }}
                      >
                        <Typography
                          variant="caption"
                          style={{
                            color:
                              overAmount > 0
                                ? '#7e22ce'
                                : debtAmount === 0 && grandTotal > 0
                                ? '#2e7d32'
                                : paidAmount > 0
                                ? '#d35400'
                                : '#cf1322',
                            fontWeight: 600
                          }}
                        >
                          {overAmount > 0
                            ? 'Thu thừa đơn này'
                            : debtAmount === 0 && grandTotal > 0
                            ? 'Đã thanh toán đủ'
                            : 'Còn phải thu đơn này'}
                        </Typography>
                        <Typography
                          style={{
                            fontSize: 15,
                            fontWeight: 700,
                            color:
                              overAmount > 0
                                ? '#7e22ce'
                                : debtAmount === 0 && grandTotal > 0
                                ? '#27ae60'
                                : paidAmount > 0
                                ? '#d35400'
                                : '#cf1322',
                            marginTop: 2
                          }}
                        >
                          {overAmount > 0 ? `+${formatMoney(overAmount)}` : formatMoney(debtAmount)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Tabs Switcher for Agency Receipts */}
                  {isAgencyCustomer && agencyInfo && (agencyInfo.receipts || []).length > 0 && (
                    <Box mb={1.5} style={{ borderBottom: '1px solid #e0e0e0' }}>
                      <Tabs
                        value={receiptTab}
                        onChange={(e, val) => setReceiptTab(val)}
                        indicatorColor="primary"
                        textColor="primary"
                      >
                        <Tab
                          label={`Toàn bộ phiếu thu Đại lý (${(agencyInfo.receipts || []).length})`}
                          style={{ textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 36, padding: '6px 12px' }}
                        />
                        <Tab
                          label={`Phiếu thu của đơn này (${localReceipts.length})`}
                          style={{ textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 36, padding: '6px 12px' }}
                        />
                      </Tabs>
                    </Box>
                  )}

                  {/* Table of receipts */}
                  {displayedReceipts.length > 0 ? (
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      style={{
                        maxHeight: 430,
                        overflowY: 'auto'
                      }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell style={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', backgroundColor: '#f5f6fa', zIndex: 2 }}>Ngày thu</TableCell>
                            <TableCell style={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', backgroundColor: '#f5f6fa', zIndex: 2 }}>Mã phiếu</TableCell>
                            <TableCell style={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', backgroundColor: '#f5f6fa', zIndex: 2 }} align="right">Số tiền (VNĐ)</TableCell>
                            <TableCell style={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', backgroundColor: '#f5f6fa', zIndex: 2 }}>Tài khoản nhận</TableCell>
                            <TableCell style={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', backgroundColor: '#f5f6fa', zIndex: 2 }}>Người lập</TableCell>
                            {effectiveCanManageReceipts && (
                              <TableCell style={{ fontWeight: 700, fontSize: 12, width: 80, whiteSpace: 'nowrap', backgroundColor: '#f5f6fa', zIndex: 2 }} align="center">Thao tác</TableCell>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {displayedReceipts.map((r, index) => {
                            const rDate = r.receipt_date || r.date;
                            const rCode = r.code || `PT #${r.id || index + 1}`;
                            const rAmount = Number(r.amount) || 0;
                            const rAccount = r.account || r.target_account || 'Tiền mặt';
                            const creator = r.creator?.full_name || r.created_by || '--';

                            return (
                              <TableRow key={r.id || index} hover>
                                <TableCell style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                  {rDate ? moment(rDate).format('DD/MM/YYYY') : '--'}
                                </TableCell>
                                <TableCell style={{ fontSize: 12, fontWeight: 600, color: '#1976d2', whiteSpace: 'nowrap' }}>
                                  <span
                                    onClick={() => handleCopyText(rCode, 'mã phiếu thu')}
                                    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                  >
                                    {rCode}
                                    <FileCopyOutlinedIcon style={{ fontSize: 12, color: '#888' }} />
                                  </span>
                                  {receiptTab === 1 && (r.order_code || r.order_id) && (
                                    <div style={{ fontSize: 11, color: '#666', fontWeight: 400, marginTop: 2 }}>
                                      Đơn #{r.order_code || r.order_id}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell align="right" style={{ fontSize: 13, fontWeight: 700, color: '#2e7d32', whiteSpace: 'nowrap' }}>
                                  {formatMoney(rAmount)}
                                </TableCell>
                                <TableCell style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                  <Chip
                                    label={rAccount}
                                    size="small"
                                    style={{
                                      height: 20,
                                      fontSize: 10.5,
                                      backgroundColor: rAccount === 'Tiền mặt' ? '#e8f0fe' : '#eaf4fb',
                                      color: rAccount === 'Tiền mặt' ? '#1a73e8' : '#1976d2'
                                    }}
                                  />
                                </TableCell>
                                <TableCell style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>
                                  {creator}
                                </TableCell>
                                {effectiveCanManageReceipts && (
                                  <TableCell align="center" style={{ width: 80, whiteSpace: 'nowrap' }}>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleReceiptMenuOpen(e, r)}
                                    >
                                      <MoreVertIcon fontSize="small" style={{ color: '#666' }} />
                                    </IconButton>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box
                      p={2}
                      style={{
                        backgroundColor: '#fafafa',
                        border: '1px dashed #e0e0e0',
                        borderRadius: 8,
                        textAlign: 'center'
                      }}
                    >
                      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                        {isAgencyCustomer
                          ? (receiptTab === 0 ? 'Chưa có phiếu thu nào cho đại lý này.' : 'Chưa có phiếu thu nào gắn với đơn này.')
                          : 'Chưa có phiếu thu nào được lập cho đơn hàng này.'}
                      </Typography>
                      {effectiveCanManageReceipts && order?.status !== -1 && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleCreateReceipt}
                          style={{
                            textTransform: 'none',
                            color: '#2e7d32',
                            borderColor: '#2e7d32',
                            fontWeight: 600
                          }}
                        >
                          Lập phiếu thu
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions style={{ padding: '12px 24px', borderTop: '1px solid #e0e3ef' }}>
          <Button
            onClick={onClose}
            variant="contained"
            style={{
              backgroundColor: '#d32f2f',
              color: '#ffffff',
              fontWeight: 700,
              padding: '6px 24px',
              borderRadius: 4
            }}
          >
            Đóng
          </Button>
        </DialogActions>

        {/* ── Menu Thao Tác Phiếu Thu ── */}
        <Menu
          anchorEl={receiptMenuAnchor}
          keepMounted
          open={Boolean(receiptMenuAnchor)}
          onClose={handleReceiptMenuClose}
          PaperProps={{
            style: {
              minWidth: 140,
              borderRadius: 8,
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
            }
          }}
        >
          <MenuItem
            onClick={() => {
              const r = selectedReceipt;
              handleReceiptMenuClose();
              if (onEditReceipt && r) {
                onClose();
                onEditReceipt(r);
              }
            }}
            style={{ fontSize: 13 }}
          >
            <ListItemIcon style={{ minWidth: 30, color: '#1976d2' }}>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Chỉnh sửa" primaryTypographyProps={{ style: { fontSize: 13, fontWeight: 500 } }} />
          </MenuItem>
          <MenuItem
            onClick={() => {
              const r = selectedReceipt;
              handleReceiptMenuClose();
              if (onDeleteReceipt && r) {
                onClose();
                onDeleteReceipt(r);
              }
            }}
            style={{ fontSize: 13, color: '#d32f2f' }}
          >
            <ListItemIcon style={{ minWidth: 30, color: '#d32f2f' }}>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Xóa phiếu" primaryTypographyProps={{ style: { fontSize: 13, fontWeight: 500, color: '#d32f2f' } }} />
          </MenuItem>
        </Menu>
      </Dialog>

      {/* Full View Serial Modal */}
      <Dialog
        open={serialModal.open}
        onClose={() => setSerialModal({ open: false, typeName: '', serials: [] })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle disableTypography style={{ padding: '16px 20px', borderBottom: '1px solid #e0e3ef' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" style={{ fontWeight: 700, fontSize: 16, color: '#1a1f36' }}>
                Danh sách Serial / IMEI
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ fontSize: 12 }}>
                Loại thiết bị: <strong style={{ color: '#1a73e8' }}>{serialModal.typeName}</strong> ({serialModal.serials.length} thiết bị)
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setSerialModal({ open: false, typeName: '', serials: [] })}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent style={{ padding: '16px 20px' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} style={{ gap: 12 }}>
            <TextField
              placeholder="Tìm kiếm Serial / IMEI..."
              variant="outlined"
              size="small"
              fullWidth
              value={serialSearch}
              onChange={e => setSerialSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ fontSize: 18, color: '#888' }} />
                  </InputAdornment>
                )
              }}
            />
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<FileCopyIcon />}
              onClick={() => {
                const textToCopy = filteredSerials.join('\n');
                navigator.clipboard.writeText(textToCopy);
                dispatch(
                  showGlobalToast({
                    message: `Đã sao chép ${filteredSerials.length} Serial vào bộ nhớ tạm!`,
                    type: messageToastType_const.success
                  })
                );
              }}
              style={{ whiteSpace: 'nowrap', textTransform: 'none', fontWeight: 600, height: 40, minWidth: 120 }}
            >
              Copy tất cả
            </Button>
          </Box>

          <Paper
            variant="outlined"
            style={{
              maxHeight: 380,
              overflowY: 'auto',
              padding: 12,
              backgroundColor: '#f8f9fa'
            }}
          >
            {filteredSerials.length > 0 ? (
              <Grid container spacing={1}>
                {filteredSerials.map((s, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Box
                      display="flex"
                      alignItems="center"
                      style={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 6,
                        padding: '6px 10px',
                        fontSize: 12
                      }}
                    >
                      <Typography
                        variant="caption"
                        style={{
                          color: '#94a3b8',
                          marginRight: 8,
                          fontWeight: 700,
                          fontSize: 11,
                          minWidth: 28
                        }}
                      >
                        #{idx + 1}
                      </Typography>
                      <Typography
                        style={{
                          fontSize: 12,
                          fontFamily: 'monospace',
                          fontWeight: 600,
                          color: '#1e293b',
                          flexGrow: 1,
                          userSelect: 'all'
                        }}
                      >
                        {s}
                      </Typography>
                      <Tooltip title="Sao chép Serial này">
                        <IconButton
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(s);
                            dispatch(
                              showGlobalToast({
                                message: `Đã sao chép: ${s}`,
                                type: messageToastType_const.success
                              })
                            );
                          }}
                          style={{ padding: 2 }}
                        >
                          <FileCopyIcon style={{ fontSize: 14, color: '#64748b' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box textAlign="center" py={4}>
                <Typography variant="body2" color="textSecondary">
                  Không tìm thấy Serial nào phù hợp
                </Typography>
              </Box>
            )}
          </Paper>
        </DialogContent>
        <DialogActions style={{ padding: '12px 20px', borderTop: '1px solid #e0e3ef' }}>
          <Button
            onClick={() => setSerialModal({ open: false, typeName: '', serials: [] })}
            variant="contained"
            style={{
              backgroundColor: '#d32f2f',
              color: '#ffffff',
              fontWeight: 700,
              padding: '6px 24px',
              borderRadius: 4
            }}
          >
            Đóng
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderDetailDialog;
